import React from "react";
import { Box, Typography } from "@mui/material";
import { PROFILE_FONT } from "../../../constants/layout";
import { REPORT_COLORS, REPORT_RADIUS, REPORT_TYPO } from "./mfReportsTheme";

type ReportColumn = { key: string; value: string };

const labelSx = {
  fontFamily: PROFILE_FONT,
  fontSize: { xs: 12, sm: 13 },
  fontWeight: 400,
  color: REPORT_COLORS.mutedText,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
  flexShrink: 0,
} as const;

const cardSx = {
  bgcolor: REPORT_COLORS.white,
  borderRadius: REPORT_RADIUS.md,
  border: `1px solid ${REPORT_COLORS.border}`,
  px: { xs: 1.5, sm: 2 },
  py: { xs: 1.5, sm: 1.75 },
  mb: 1.25,
  "&:last-child": { mb: 0 },
};

const CardMetricRow: React.FC<{
  label: string;
  value: React.ReactNode;
  allowValueWrap?: boolean;
}> = ({ label, value, allowValueWrap = false }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems={allowValueWrap ? "flex-start" : "center"}
    gap={1}
    minWidth={0}
  >
    <Typography sx={labelSx}>{label}</Typography>
    <Box
      sx={{
        fontFamily: PROFILE_FONT,
        fontSize: { xs: 12, sm: 14 },
        fontWeight: 500,
        color: REPORT_COLORS.primaryText,
        textAlign: "right",
        minWidth: 0,
        flex: allowValueWrap ? 1 : undefined,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.3,
        whiteSpace: allowValueWrap ? "normal" : "nowrap",
        wordBreak: allowValueWrap ? "break-word" : undefined,
        overflow: allowValueWrap ? "visible" : "hidden",
        textOverflow: allowValueWrap ? undefined : "ellipsis",
      }}
    >
      {value}
    </Box>
  </Box>
);

const WRAP_VALUE_KEYS = new Set([
  "schemeName",
  "holdingName",
  "isin",
  "sector",
  "symbol",
  "unrealisedPNLCombined",
  "realisedPNLCombined",
  "transactionType",
  "transType",
  "txnType",
  "type",
]);

interface ReportCardListProps {
  bodyData: Array<Record<string, unknown>>;
  columns: ReportColumn[];
  renderCell: (key: string, row: Record<string, unknown>) => React.ReactNode;
}

const ReportDataCard: React.FC<{
  row: Record<string, unknown>;
  columns: ReportColumn[];
  renderCell: (key: string, row: Record<string, unknown>) => React.ReactNode;
}> = ({ row, columns, renderCell }) => {
  const displayColumns = columns.filter((col) => col.key !== "action");
  if (displayColumns.length === 0) return null;

  const [titleColumn, ...metricColumns] = displayColumns;
  const isSchemeHeader = row._rowKind === "scheme";
  const isStockRow = row._rowKind === "stock";

  return (
    <Box
      sx={{
        ...cardSx,
        bgcolor: REPORT_COLORS.white,
        ...(isSchemeHeader
          ? {
              borderColor: REPORT_COLORS.border,
              mt: 1.5,
            }
          : null),
        ...(isStockRow
          ? {
              ml: { xs: 1, sm: 1.5 },
              borderLeft: `3px solid ${REPORT_COLORS.border}`,
            }
          : null),
      }}
    >
      <Box mb={metricColumns.length > 0 ? 1 : 0}>
        <Typography
          sx={{
            fontFamily: PROFILE_FONT,
            fontSize: { xs: 14, sm: 16 },
            fontWeight: isSchemeHeader ? 700 : 600,
            color: REPORT_COLORS.primaryText,
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {renderCell(titleColumn.key, row)}
        </Typography>
      </Box>

      {metricColumns.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.75}>
          {metricColumns.map((col) => (
            <CardMetricRow
              key={col.key}
              label={col.value}
              value={renderCell(col.key, row)}
              allowValueWrap={WRAP_VALUE_KEYS.has(col.key)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const MfReportCardList: React.FC<ReportCardListProps> = ({
  bodyData,
  columns,
  renderCell,
}) => {
  const rows = bodyData.filter((row) => row !== null && row !== undefined);

  if (!columns.length) return null;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        px: { xs: 1, sm: 2 },
        py: { xs: 1.25, sm: 2 },
        bgcolor: REPORT_COLORS.white,
      }}
    >
      {rows.map((row, index) => (
        <ReportDataCard
          key={index}
          row={row}
          columns={columns}
          renderCell={renderCell}
        />
      ))}
    </Box>
  );
};

export default MfReportCardList;
